import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { listStocktakeHistory, type StocktakeHistoryBatch } from '@/services/inventory/apiClient'
import { formatDateTimeFull } from '@/lib/dateFormat'

const fmt = (value: number) => value.toLocaleString('vi-VN', { maximumFractionDigits: 4 })

/** Mỗi lần kiểm kê: ngày, người kiểm, lý do, và từng vật tư thay đổi bao nhiêu (tồn trước → tồn sau). */
export function StocktakeHistoryPage() {
  const [batches, setBatches] = useState<StocktakeHistoryBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let active = true
    listStocktakeHistory(100)
      .then((data) => { if (active) setBatches(data) })
      .catch((err: unknown) => { if (active) setError(err instanceof Error ? err.message : 'Không tải được lịch sử kiểm kê') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  const q = query.trim().toLocaleLowerCase('vi')
  const shown = batches
    .map((batch) => ({ ...batch, items: q ? batch.items.filter((i) => i.name.toLocaleLowerCase('vi').includes(q) || batch.key.toLocaleLowerCase('vi').includes(q)) : batch.items }))
    .filter((batch) => batch.items.length > 0)

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white">
      <div className="px-3 pt-2">
        <Breadcrumb items={[{ label: 'Kho', to: '/warehouse/stock' }, { label: 'Lịch sử kiểm kê' }]} />
        <PageHeader title="Lịch sử kiểm kê" subtitle="Mỗi lần kiểm kê: vật tư nào thay đổi, tồn trước, tồn sau" />
      </div>
      <div className="flex items-center gap-3 px-3 pb-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Tìm vật tư hoặc mã phiếu…"
          className="border border-[#999] px-2 py-1 text-[12px] w-[260px] outline-none"
        />
        <Link to="/warehouse/stock" className="text-[12px] text-[var(--color-link)] underline">← Về Tồn kho</Link>
      </div>
      <div className="flex-1 min-h-0 overflow-auto px-3 pb-3">
        {loading ? <div className="p-4 text-sm text-slate-500">Đang tải…</div> : null}
        {error ? <div className="p-3 text-sm text-red-700 bg-red-50">{error}</div> : null}
        {!loading && !error && shown.length === 0 ? <div className="p-4 text-sm text-slate-500">Chưa có phiếu kiểm kê nào.</div> : null}
        {shown.map((batch) => (
          <section key={batch.key} className="mb-4">
            <div className="border border-black bg-[#f2f2f2] px-2 py-1 text-[12.5px] font-semibold flex flex-wrap gap-x-4 gap-y-0.5">
              <span>{batch.key}</span>
              <span>{formatDateTimeFull(batch.created_at)}</span>
              <span>Người kiểm: {batch.created_by}</span>
              <span>{batch.items.length} vật tư thay đổi</span>
              <span className="font-normal">Lý do: {batch.reason}</span>
            </div>
            <table className="w-full border-collapse text-[12.5px] table-fixed">
              <colgroup>
                <col style={{ width: '4%' }} /><col style={{ width: '52%' }} /><col style={{ width: '6%' }} />
                <col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr className="[&>th]:border [&>th]:border-black [&>th]:bg-[#f2f2f2] [&>th]:px-2 [&>th]:py-1">
                  <th>STT</th><th className="text-left">Tên chi tiết</th><th>ĐVT</th><th className="text-right">Tồn trước</th><th className="text-right">Tồn sau</th><th className="text-right">Chênh lệch</th>
                </tr>
              </thead>
              <tbody className="[&_td]:border [&_td]:border-black [&_td]:px-2 [&_td]:py-1 [&_td]:break-words">
                {batch.items.map((item, index) => (
                  <tr key={`${item.material_id}-${item.stock_status}`}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item.name}</td>
                    <td className="text-center">{item.uom}</td>
                    <td className="text-right">{fmt(item.before)}</td>
                    <td className="text-right">{fmt(item.after)}</td>
                    <td className={`text-right font-semibold ${item.delta < 0 ? 'text-red-700' : 'text-green-700'}`}>{item.delta > 0 ? '+' : ''}{fmt(item.delta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  )
}
